// ============================================================
// TARGET PATH IN YOUR REPO:
//   app/src/app/components/skyplus/indexing/document-type-registry.service.ts
// ============================================================
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  WfDocumentTypeApiService,
  DocumentType as RegistryType,
  DocumentTypeField as RegistryField
} from '../wf-runtime/services/wf-document-type-api.service';
import {
  DocumentTypeConfig,
  DocumentField,
  FieldType,
  setRuntimeDocumentTypes,
  clearRuntimeDocumentTypes
} from './document-types.config';

/**
 * Strike D — bridges the document-type registry (GET /api/wf/document-types)
 * into the existing static-config consumers.
 *
 * <p>{@link #load} fetches the registry, maps each {@link RegistryType} into the
 * legacy {@link DocumentTypeConfig} shape the capture screen / indexing service
 * already understand, and installs them via {@code setRuntimeDocumentTypes}.
 * After that, the existing helper functions ({@code getEnabledDocumentTypes},
 * {@code getDocumentTypeById}, {@code getFolderCreationFields}, …) transparently
 * return registry data — no call site changes.
 *
 * <h2>Safety</h2>
 * If the registry is empty or the request fails for any reason, the runtime set
 * is cleared and the built-in static {@code DOCUMENT_TYPES} remain in force.
 * {@link #load} never rejects — capture must keep working regardless.
 *
 * <h2>Folder mapping</h2>
 * The registry models folders as ordered {@code folderSegment}s. The current
 * upload path supports one MAIN folder + one SUBFOLDER, so this maps the
 * lowest-order segment to {@code canCreateFolder} and the next to
 * {@code canCreateSubfolder}. Full multi-segment paths land in Strike E
 * (folder-placement engine); third+ segments are ignored for now.
 */
@Injectable({ providedIn: 'root' })
export class WfDocumentTypeRegistryService {

  private readonly api = inject(WfDocumentTypeApiService);

  private loadedOnce = false;

  /** True once a load has completed (whether from registry or fallback). */
  get loaded(): boolean { return this.loadedOnce; }

  /**
   * Fetch + install registry types. Resolves when done (or on fallback).
   * Idempotent: re-uses the previous result unless {@code force} is true.
   */
  async load(force = false): Promise<void> {
    if (this.loadedOnce && !force) { return; }
    try {
      const summaries = await firstValueFrom(
        this.api.listAll(false).pipe(catchError(() => of([])))
      );
      if (!summaries || !summaries.length) {
        clearRuntimeDocumentTypes();
        this.loadedOnce = true;
        return;
      }
      const fulls = await firstValueFrom(
        forkJoin(summaries.map(s => this.api.getOrNull(s.typeKey)))
      );
      const configs = (fulls ?? [])
        .filter((t): t is RegistryType => !!t)
        .map(t => this.toConfig(t));
      if (configs.length) {
        setRuntimeDocumentTypes(configs);
      } else {
        clearRuntimeDocumentTypes();
      }
    } catch {
      // Any failure → keep the static fallback. Never break capture.
      clearRuntimeDocumentTypes();
    } finally {
      this.loadedOnce = true;
    }
  }

  /** Drop a single cached type and force the next load to refetch. */
  invalidate(typeKey?: string): void {
    this.api.invalidate(typeKey);
    this.loadedOnce = false;
  }

  // ── Mapping ────────────────────────────────────────────────────────

  private toConfig(t: RegistryType): DocumentTypeConfig {
    const aspectNames = t.aspectNames ?? [];

    // Resolve folder ordering → main + first subfolder.
    const folderFields = (t.fields ?? [])
      .filter(f => f.folderSegment?.enabled)
      .sort((a, b) => (a.folderSegment?.order ?? 0) - (b.folderSegment?.order ?? 0));
    const mainFolderProp = folderFields[0]?.propertyName;
    const subFolderProp  = folderFields[1]?.propertyName;

    const fields: DocumentField[] = (t.fields ?? []).map(f =>
      this.toField(f, f.propertyName === mainFolderProp, f.propertyName === subFolderProp)
    );

    return {
      id: t.typeKey,
      name: t.label,
      description: t.description ?? '',
      nodeType: t.nodeType,
      aspectName: aspectNames[0] ?? '',
      aspectNames,
      icon: t.icon || 'description',
      color: t.color || '#4A90D9',
      baseFolderPath: t.baseFolderPath,
      enabled: t.active !== false,
      sortOrder: t.sortOrder ?? 0,
      // Round 7 — without these two the folder-rule engine is invisible to
      // every capture flow: the FSI and Upload & Index read the type through
      // THIS mapping, not the raw API object.
      typeKey: t.typeKey,
      folderRules: (t as any).folderRules,
      fields
    };
  }

  private toField(f: RegistryField, _isMainFolder: boolean, _isSubFolder: boolean): DocumentField {
    return {
      propertyName: f.propertyName,
      label: f.label,
      type: this.mapType(f.type),
      mandatory: !!f.mandatory,
      capture: f.capture !== false,
      search: f.search !== false,
      // Show as a document-library list column when ticked in the studio (opt-in, default off).
      column: f.column === true,
      // Strike E — registry types file via ordered folderSegment (config-driven),
      // not the legacy operator-picked canCreateFolder/canCreateSubfolder fields.
      folderSegment: f.folderSegment?.enabled
        ? { enabled: true, order: f.folderSegment.order ?? 0, useLabel: f.folderSegment.useLabel !== false }
        : undefined,
      subfolderUseLabel: f.folderSegment?.useLabel,
      placeholder: f.placeholder,
      defaultValue: f.defaultValue,
      options: f.options,
      pattern: f.pattern,
      maxLength: f.maxLength,
      autoPopulateUserId: f.autoPopulateUserId,
      autoPopulateDate: f.autoPopulateDate
    };
  }

  /** Registry has a 'boolean' type the legacy form doesn't render; treat as text. */
  private mapType(t: string): FieldType {
    switch (t) {
      case 'date':     return 'date';
      case 'number':   return 'number';
      case 'select':   return 'select';
      case 'textarea': return 'textarea';
      default:         return 'text';
    }
  }
}